"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PLOT_HEIGHT,
  PLOT_WIDTH,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import { equalizeAspect, padRange } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

export interface XY {
  x: number;
  y: number;
}

export interface Stage {
  points: XY[];
  color: string;
  /** Faint dashed outline for the steps in between. */
  faint?: boolean;
  /** Names drawn beside the vertices (A, B, C … or A′, B′, C′ …). */
  labels?: string[];
}

export type Guide =
  | { kind: "line"; m: number; b: number }
  | { kind: "vertical"; c: number }
  | { kind: "point"; at: XY };

function pathOf(
  points: XY[],
  x: (v: number) => number,
  y: (v: number) => number
): string {
  const commands = points.map(
    (p, i) => `${i === 0 ? "M" : "L"}${x(p.x).toFixed(2)} ${y(p.y).toFixed(2)}`
  );
  return `${commands.join(" ")}${points.length >= 3 ? " Z" : ""}`;
}

/** The shapes before, between and after the transformation, to scale, with guide lines. */
export function TransformPlot({
  stages,
  guides,
  title,
  downloadLabel,
}: {
  stages: Stage[];
  guides: Guide[];
  title: string;
  downloadLabel: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const all = [
    ...stages.flatMap((s) => s.points),
    ...guides.flatMap((g) => (g.kind === "point" ? [g.at] : [])),
  ];
  const xs = [0, ...all.map((p) => p.x)];
  const ys = [0, ...all.map((p) => p.y)];
  const span = Math.max(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
    1
  );
  const [xMin, xMax] = padRange(Math.min(...xs), Math.max(...xs), 0.18, span);
  const [yMin, yMax] = padRange(Math.min(...ys), Math.max(...ys), 0.18, span);
  const viewport = equalizeAspect(
    { xMin, xMax, yMin, yMax },
    PLOT_WIDTH,
    PLOT_HEIGHT
  );
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={viewport}
      >
        {(scale) => (
          <>
            {guides.map((g) => {
              if (g.kind === "point") {
                return (
                  <circle
                    cx={scale.x(g.at.x)}
                    cy={scale.y(g.at.y)}
                    fill="none"
                    key={`p${g.at.x},${g.at.y}`}
                    r={6}
                    stroke={CHART_COLORS[2]}
                    strokeWidth={2}
                  />
                );
              }
              const line =
                g.kind === "vertical"
                  ? { x1: g.c, y1: viewport.yMin, x2: g.c, y2: viewport.yMax }
                  : {
                      x1: viewport.xMin,
                      y1: g.m * viewport.xMin + g.b,
                      x2: viewport.xMax,
                      y2: g.m * viewport.xMax + g.b,
                    };
              return (
                <line
                  key={`l${line.x1},${line.y1},${line.x2},${line.y2}`}
                  stroke={CHART_COLORS[2]}
                  strokeDasharray="7 5"
                  strokeWidth={1.75}
                  x1={scale.x(line.x1)}
                  x2={scale.x(line.x2)}
                  y1={scale.y(line.y1)}
                  y2={scale.y(line.y2)}
                />
              );
            })}
            {stages.map((stage, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: stages are an ordered sequence
              <g key={index}>
                <path
                  d={pathOf(stage.points, scale.x, scale.y)}
                  fill={stage.faint ? "none" : `${stage.color}22`}
                  stroke={stage.color}
                  strokeDasharray={stage.faint ? "3 4" : undefined}
                  strokeWidth={stage.faint ? 1.25 : 2.5}
                />
                {stage.faint
                  ? null
                  : stage.points.map((p, i) => (
                      <g key={`${p.x},${p.y},${stage.labels?.[i] ?? i}`}>
                        <circle
                          cx={scale.x(p.x)}
                          cy={scale.y(p.y)}
                          fill={stage.color}
                          r={4}
                        />
                        {stage.labels?.[i] ? (
                          <text
                            fill={CHART_INK.text}
                            fontSize={12}
                            fontWeight={600}
                            x={scale.x(p.x) + 7}
                            y={scale.y(p.y) - 7}
                          >
                            {stage.labels[i]}
                          </text>
                        ) : null}
                      </g>
                    ))}
              </g>
            ))}
          </>
        )}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("transformation.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
